import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import type {
  DashboardModel,
  DashboardRequestBody,
  DashboardsResponseModel,
  GetDashboardsRequestParams,
  GetReportRequestParams,
  GetShareableLinksRequestParams,
  PostDashboardRequestParams,
  ReportResponseModel,
  ShareableDashboardLinkListResponseModel,
  ShareableDashboardLinkModel,
} from '@/apis/dashboards.types';
import { env } from '@/env';
import type { HttpRequestConfig } from '@/types/http';

export type * from '@/apis/dashboards.types';

export const http = createDashHttpClient(env.dashboardApiUrl);

interface CancelKey {
  brandIds?: number[] | number | string;
  competitorSourceAccountIds?: string[] | string;
  channels?: string[] | string;
  metrics?: string[] | string;
  reportType?: string;
  aggregateBy?: string;
  sourceAdAccountIds?: string[] | string;
  sourceCampaignIds?: string[] | string;
}

const abortControllers = new Map<string, AbortController>();

function getAndSetSignal(cancelData: CancelKey): AbortSignal {
  const key = JSON.stringify(cancelData);
  abortControllers.get(key)?.abort();
  const controller = new AbortController();
  abortControllers.set(key, controller);
  return controller.signal;
}

const api = {
  // Dashboards
  getDashboards(params?: GetDashboardsRequestParams) {
    return http.get<DashboardsResponseModel>('/dashboards', {
      params,
    });
  },
  getDashboard({ dashboardId }: { dashboardId: number }) {
    return http.get<DashboardModel>(`/dashboards/${dashboardId}`);
  },
  createDashboard({
    data,
    params,
  }: {
    data: DashboardRequestBody;
    params?: PostDashboardRequestParams;
  }) {
    return http.post<DashboardModel>(
      '/dashboards',
      {
        ...data,
      },
      {
        params,
      },
    );
  },
  updateDashboard({
    dashboardId,
    data,
  }: {
    dashboardId: number;
    data: Partial<DashboardRequestBody>;
  }) {
    return http.patch<DashboardModel>(`/dashboards/${dashboardId}`, {
      ...data,
    });
  },
  deleteDashboard({ dashboardId }: { dashboardId: number }) {
    return http.delete<void>(`/dashboards/${dashboardId}`);
  },
  // Reports
  // TODO(spec): /metric_types is not in dashboard.openapi.json — request backend add operation.
  getMetricTypes() {
    return http.get('/metric_types');
  },
  // TODO(spec): /metric_details is not in dashboard.openapi.json.
  // TODO(types): convert in follow-up PR.
  getMetricDetails(params: Record<string, unknown>, config: HttpRequestConfig = {}) {
    return http.get('/metric_details', merge({ params }, config));
  },
  // TODO(spec): /comparable_metrics is not in dashboard.openapi.json.
  getComparableMetrics() {
    return http.get(`/comparable_metrics`);
  },
  getReport({
    dashboardId,
    reportId,
    signal,
    params,
  }: {
    dashboardId: number;
    reportId: number;
    signal?: AbortSignal;
    params?: GetReportRequestParams;
  }) {
    return http.get<ReportResponseModel>(`/dashboards/${dashboardId}/reports/${reportId}`, {
      params,
      signal,
    });
  },
  // TODO(types): convert in follow-up PR — load-bearing endpoint with PUT-vs-GET overlap.
  getGenericReportData(
    {
      brandIds,
      competitorSourceAccountIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      channels,
      metrics,
      reportType,
      timeScale,
      sortOrder,
      limit,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
      sourceAdCampaigns,
    }: {
      brandIds?: number[] | number;
      competitorSourceAccountIds?: string[];
      startDate?: string;
      endDate?: string;
      contextStartDate?: string;
      contextEndDate?: string;
      channels?: string[] | string;
      metrics?: string[] | string;
      reportType?: string;
      timeScale?: string;
      sortOrder?: string;
      limit?: number;
      aggregateBy?: string;
      sourceAdAccountIds?: string[];
      sourceCampaignIds?: string[];
      sourceAdCampaigns?: unknown[];
    },
    config: HttpRequestConfig = {},
  ) {
    const signal = getAndSetSignal({
      brandIds,
      competitorSourceAccountIds,
      channels,
      metrics,
      reportType,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
    });

    return http.put(
      '/reports/data',
      {
        competitor_source_accountIds: competitorSourceAccountIds,
        source_ad_account_ids: sourceAdAccountIds,
        source_campaign_ids: sourceCampaignIds,
        source_ad_campaigns: sourceAdCampaigns,
      },
      merge(
        {
          params: {
            brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
            startDate,
            endDate,
            contextStartDate,
            contextEndDate,
            channels: Array.isArray(channels) ? channels.join(',') : channels,
            metrics: Array.isArray(metrics) ? metrics.join(',') : metrics,
            reportType,
            timeScale,
            sortOrder,
            limit,
            aggregateBy,
          },
          signal,
        },
        config,
      ),
    );
  },
  // TODO(types): convert in follow-up PR.
  createReport({ dashboardId, data }: { dashboardId: number; data: Record<string, unknown> }) {
    return http.post(`/dashboards/${dashboardId}/reports`, {
      ...data,
    });
  },
  // TODO(types): convert in follow-up PR.
  updateReport({
    dashboardId,
    reportId,
    data,
  }: {
    dashboardId: number;
    reportId: number;
    data: Record<string, unknown>;
  }) {
    return http.patch(`/dashboards/${dashboardId}/reports/${reportId}`, {
      ...data,
    });
  },
  deleteReport({ dashboardId, reportId }: { dashboardId: number; reportId: number }) {
    return http.delete<void>(`/dashboards/${dashboardId}/reports/${reportId}`);
  },
  // TODO(types): convert in follow-up PR — overlaps with getGenericReportData.
  getTopLineStatsForChannel({
    brandIds,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    channels,
    metrics,
    requirePosts,
    aggregateBy = 'BRAND',
    reportType = 'TOTAL_GROUPED_METRIC',
  }: {
    brandIds?: number[] | number;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    channels?: string[] | string;
    metrics?: string[] | string;
    requirePosts?: boolean;
    aggregateBy?: string;
    reportType?: string;
  } = {}) {
    const signal = getAndSetSignal({
      brandIds,
      channels,
      metrics,
    });

    const params = {
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      requirePosts,
      metrics,
      channels,
      reportType,
      aggregateBy,
    };
    return http.get(`/reports/data`, { params, signal });
  },
  // TODO(types): convert in follow-up PR.
  getGraphStats(
    {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
    }: {
      brandIds?: number[] | number;
      startDate?: string;
      endDate?: string;
      metrics?: string[] | string;
      channels?: string[] | string;
      timeScale?: string;
    } = {},
    { signal }: { signal?: AbortSignal } = {},
  ) {
    const reportType = 'GRAPH';
    const cancelOptions = signal
      ? { signal }
      : {
          signal: getAndSetSignal({
            brandIds,
            channels,
            metrics,
            reportType,
          }),
        };

    const params = {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      reportType,
    };
    return http.get(`/reports/data`, { params, ...cancelOptions });
  },
  // TODO(types): convert in follow-up PR.
  getBenchmarkGraphStats(
    {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      benchmarks,
    }: {
      brandIds?: number[] | number;
      startDate?: string;
      endDate?: string;
      metrics?: string[] | string;
      channels?: string[] | string;
      timeScale?: string;
      benchmarks?: unknown;
    } = {},
    { signal }: { signal?: AbortSignal } = {},
  ) {
    const params = {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      reportType: 'GRAPH',
      use_benchmark_cache: true,
    };
    return http.put(`/reports/data`, { benchmarks }, { params, signal });
  },
  // Exports
  // TODO(types): convert in follow-up PR.
  getDashboardAutomatedExports({ dashboardId }: { dashboardId: number }) {
    return http.get(`/dashboards/${dashboardId}/automated_exports`);
  },
  // TODO(types): convert in follow-up PR.
  getAllDashboardAutomatedExports() {
    return http.get(`/dashboards/automated_exports`);
  },
  // TODO(types): convert in follow-up PR.
  createDashboardAutomatedExports({
    dashboardId,
    autoExport,
  }: {
    dashboardId: number;
    autoExport: Record<string, unknown>;
  }) {
    return http.post(`/dashboards/${dashboardId}/automated_exports`, autoExport);
  },
  // TODO(types): convert in follow-up PR.
  updateDashboardAutomatedExports({
    dashboardId,
    autoExportId,
    autoExport,
  }: {
    dashboardId: number | undefined;
    autoExportId: number | undefined;
    autoExport: object;
  }) {
    return http.patch(`/dashboards/${dashboardId}/automated_exports/${autoExportId}`, autoExport);
  },
  deleteAutomatedExport({
    dashboardId,
    autoExportId,
  }: {
    dashboardId: number | undefined;
    autoExportId: number | undefined;
  }) {
    return http.delete<void>(`/dashboards/${dashboardId}/automated_exports/${autoExportId}`);
  },
  deleteDashboardAutomatedExportUser({
    dashboardId,
    autoExportId,
    userId,
  }: {
    dashboardId: number | undefined;
    autoExportId: number | undefined;
    userId: number;
  }) {
    return http.delete<void>(
      `/dashboards/${dashboardId}/automated_exports/${autoExportId}/recipients/${userId}`,
    );
  },
  // TODO(types): convert in follow-up PR — blob response.
  async getReportCsv({
    dashboardId,
    reportId,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    timeScale,
    filterOverrides = {},
  }: {
    dashboardId: number;
    reportId: number;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    timeScale?: string | null;
    filterOverrides?: object;
  }) {
    const params = {
      start_date: startDate,
      end_date: endDate,
      context_start_date: contextStartDate,
      context_end_date: contextEndDate,
      time_scale: timeScale,
      format: 'csv',
      ...filterOverrides,
    };
    return http.get<Blob>(`/dashboards/${dashboardId}/reports/${reportId}`, {
      params,
      responseType: 'blob',
    });
  },
  // TODO(types): convert in follow-up PR — blob response.
  getReportDataCsv({
    brandIds,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    channels,
    metrics,
    reportType,
    sortOrder,
    limit,
    timeScale,
    filenamePrefix,
    csvLayout,
  }: {
    brandIds?: number[] | number | string;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    channels?: string[] | string;
    metrics?: string[] | string;
    reportType?: string;
    sortOrder?: string;
    limit?: number;
    timeScale?: string;
    filenamePrefix?: string;
    csvLayout?: string;
  }) {
    return http.get<Blob>(
      `/reports/data`,
      merge(
        {
          params: {
            brandIds,
            startDate,
            endDate,
            contextStartDate,
            contextEndDate,
            channels,
            metrics,
            reportType,
            sortOrder,
            limit,
            timeScale,
            filenamePrefix,
            csvLayout,
          },
        },
        {
          params: { format: 'csv' },
          responseType: 'blob' as const,
        },
      ),
    );
  },
  // TODO(types): convert in follow-up PR — overlaps with getDashboard via the format query param.
  getDashboardSpreadsheet({
    dashboardId,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    format,
    socketId,
    timeScale,
    filterOverrides = {},
  }: {
    dashboardId: number;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    format?: string;
    socketId?: string | null;
    timeScale?: string | null;
    filterOverrides?: object;
  }) {
    const params = {
      start_date: startDate,
      end_date: endDate,
      context_start_date: contextStartDate,
      context_end_date: contextEndDate,
      time_scale: timeScale,
      socket_id: socketId,
      format,
      ...filterOverrides,
    };
    return http.get(`/dashboards/${dashboardId}`, { params });
  },
  // Sharing
  // TODO(types): convert in follow-up PR.
  getDashboardUsers({ dashboardId }: { dashboardId: number | undefined }) {
    return http.get(`/dashboards/${dashboardId}/users`);
  },
  // TODO(types): convert in follow-up PR.
  shareDashboard({
    dashboardId,
    userId,
    userRole,
  }: {
    dashboardId: number | undefined;
    userId: number | undefined;
    userRole: string;
  }) {
    return http.put(`/dashboards/${dashboardId}/users/${userId}`, {
      user_role: userRole,
    });
  },
  // TODO(types): convert in follow-up PR.
  updateDashboardOwner({
    dashboardId,
    userId,
  }: {
    dashboardId: number | undefined;
    userId: number | undefined;
  }) {
    return http.put(`/dashboards/${dashboardId}/owner`, {
      user_id: userId,
    });
  },
  deleteDashboardUser({
    dashboardId,
    userId,
  }: {
    dashboardId: number | undefined;
    userId: number | undefined;
  }) {
    return http.delete<void>(`/dashboards/${dashboardId}/users/${userId}`);
  },
  // Industries
  // TODO(spec): /industries is not in dashboard.openapi.json — only /industries/{id}/accounts is.
  getIndustries(includeFollowerBuckets = true) {
    return http.get(`/industries?include_follower_buckets=${includeFollowerBuckets}`);
  },
  // TODO(spec): /industries/source_account is not in dashboard.openapi.json.
  async getIndustriesBySourceAccount(sourceAccountId: number) {
    return http.get(`/industries/source_account?source_account_id=${sourceAccountId}`);
  },
  // TODO(spec): /benchmark/industries is not in dashboard.openapi.json.
  getHubspotIndustries() {
    return http.get('/benchmark/industries');
  },

  // Custom Metrics
  // TODO(types): convert in follow-up PR — schemas (CustomMetricCreate, CustomMetricUpdate, CustomMetricResponse) exist.
  createCustomMetric({
    organizationId,
    params,
  }: {
    organizationId: number;
    params: Record<string, unknown>;
  }) {
    return http.post(`organizations/${organizationId}/custom_metric`, params);
  },

  // TODO(types): convert in follow-up PR.
  getCustomMetricsUsage({ brandIds }: { brandIds: number[] }) {
    return http.post(`/custom_metrics/usage`, { brandIds });
  },

  // TODO(types): convert in follow-up PR.
  getCustomMetrics({
    organizationIds,
    brandIds,
    params,
  }: {
    organizationIds?: number[];
    brandIds?: number[];
    params?: Record<string, unknown>;
  }) {
    let payload: { organizationIds: number[] } | { brandIds: number[] } | undefined;
    if (organizationIds) payload = { organizationIds };
    else if (brandIds) payload = { brandIds };
    return http.post('/custom_metrics', payload, {
      params,
    });
  },

  // TODO(types): convert in follow-up PR.
  updateCustomMetric({
    organizationId,
    metricId,
    params,
  }: {
    organizationId: number;
    metricId: number;
    params: Record<string, unknown>;
  }) {
    return http.patch(`organizations/${organizationId}/custom_metric/${metricId}`, params);
  },
  deleteCustomMetric({ organizationId, metricId }: { organizationId: number; metricId: number }) {
    return http.delete<void>(`organizations/${organizationId}/custom_metric/${metricId}`);
  },
  // Shareable Dashboard Links
  getShareableDashboardLinks({ userId, dashboardId, brandId }: GetShareableLinksRequestParams) {
    const params = {
      userId,
      dashboardId,
      brandId,
    };
    return http.get<ShareableDashboardLinkListResponseModel>(`/shareable_links`, {
      params,
      camelizeKeys: true,
    });
  },
  getShareableDashboardLink({ shareableLinkId }: { shareableLinkId: number }) {
    return http.get<ShareableDashboardLinkModel>(`/shareable_links/${shareableLinkId}`, {
      camelizeKeys: true,
    });
  },
  // TODO(types): convert in follow-up PR — request body shape needs CamelizeKeys<CreateShareableDashboardLink>.
  createShareableDashboardLink({
    dashboardId,
    name,
    allowReportingPeriodChange,
    allowFilterChange,
    validUntil,
    defaultFilters,
  }: {
    dashboardId: number;
    name: string;
    allowReportingPeriodChange?: boolean;
    allowFilterChange?: boolean;
    validUntil?: string;
    defaultFilters?: string;
  }) {
    const data = {
      dashboardId,
      name,
      allowReportingPeriodChange,
      allowFilterChange,
      validUntil,
      defaultFilters,
    };
    return http.post('/shareable_links', data, { camelizeKeys: true });
  },
  // TODO(types): convert in follow-up PR.
  updateShareableDashboardLink({
    shareableLinkId,
    name,
    enabled,
    validUntil,
    allowReportingPeriodChange,
    allowFilterChange,
    defaultFilters,
  }: {
    shareableLinkId: number;
    name?: string;
    enabled?: boolean;
    validUntil?: string;
    allowReportingPeriodChange?: boolean;
    allowFilterChange?: boolean;
    defaultFilters?: string;
  }) {
    const data = {
      name,
      enabled,
      validUntil,
      allowReportingPeriodChange,
      allowFilterChange,
      defaultFilters,
    };
    return http.patch(`/shareable_links/${shareableLinkId}`, data, { camelizeKeys: true });
  },
  deleteShareableDashboardLink({ shareableLinkId }: { shareableLinkId: number }) {
    return http.delete<void>(`/shareable_links/${shareableLinkId}`, { camelizeKeys: true });
  },
};

export default api;
